<template>
	<div>
		<v-tooltip bottom>
			<template #activator="{ on, attrs }">
				<v-icon
					v-bind="attrs"
					class="content-publisher-btn"
					v-on="on"
					@click="openDialog"
				>
					{{ icons.mdiPlusCircleOutline }}
				</v-icon>
			</template>
			<span>Publish content</span>
		</v-tooltip>

		<v-dialog
			v-model="dialog"
			max-width="600"
			persistent
			@keydown.esc="closeDialog"
		>
			<v-card :key="dialogKey">
				<v-card-title class="text-subtitle-1 font-weight-semibold pb-2">
					<v-icon
						class="me-2"
						small
					>
						{{ icons.mdiPlusCircleOutline }}
					</v-icon>
					Publish content
				</v-card-title>

				<v-card-text>
					<!-- Content type -->
					<v-btn-toggle
						v-model="contentType"
						dense
						mandatory
						class="mb-4"
						color="primary"
					>
						<v-btn
							small
							value="feed"
						>
							<v-icon
								small
								class="me-1"
							>
								{{ icons.mdiViewDashboardOutline }}
							</v-icon>
							Feed Post
						</v-btn>
						<v-btn
							small
							value="story"
						>
							<v-icon
								small
								class="me-1"
							>
								{{ icons.mdiBookOpenPageVariantOutline }}
							</v-icon>
							Story
						</v-btn>
					</v-btn-toggle>

					<!-- Account -->
					<network-account-select
						v-model="form.userId"
						label-text="Publish as"
						class="mb-4"
					/>

					<!-- Content / Caption -->
					<v-textarea
						v-model="form.content"
						:label="contentType === 'story' ? 'Caption' : 'Content'"
						:placeholder="contentType === 'story' ? 'Optional caption...' : 'Write the post content...'"
						outlined
						dense
						:counter="contentType === 'story' ? 280 : 600"
						:maxlength="contentType === 'story' ? 280 : 600"
						rows="3"
						:error-messages="errors.content ?? errors.caption"
						hide-details="auto"
						class="mb-4"
					/>

					<!-- Media Type -->
					<v-btn-toggle
						v-model="mediaType"
						dense
						class="mb-4"
						color="primary"
					>
						<v-btn
							v-if="contentType === 'feed'"
							small
							value="text"
						>
							<v-icon
								small
								class="me-1"
							>
								{{ icons.mdiText }}
							</v-icon>
							Text
						</v-btn>
						<v-btn
							small
							value="image"
						>
							<v-icon
								small
								class="me-1"
							>
								{{ icons.mdiImage }}
							</v-icon>
							Image
						</v-btn>
						<v-btn
							v-if="contentType === 'feed'"
							small
							value="gif"
						>
							<v-icon
								small
								class="me-1"
							>
								{{ icons.mdiGif }}
							</v-icon>
							GIF
						</v-btn>
						<v-btn
							small
							:value="contentType === 'story' ? 'video_url' : 'youtube'"
						>
							<v-icon
								small
								class="me-1"
							>
								{{ icons.mdiYoutube }}
							</v-icon>
							{{ contentType === 'story' ? 'Video URL' : 'YouTube' }}
						</v-btn>
						<v-btn
							v-if="contentType === 'feed'"
							small
							value="video"
						>
							<v-icon
								small
								class="me-1"
							>
								{{ icons.mdiVideo }}
							</v-icon>
							Video
						</v-btn>
					</v-btn-toggle>

					<!-- Image Upload -->
					<template v-if="mediaType === 'image'">
						<v-file-input
							v-model="form.image"
							label="Image"
							accept="image/*"
							outlined
							dense
							prepend-icon=""
							:prepend-inner-icon="icons.mdiImage"
							:error-messages="errors.image"
							hide-details="auto"
							class="mb-4"
							@change="onImageChange"
						/>
						<v-img
							v-if="imagePreview"
							:src="imagePreview"
							max-height="200"
							contain
							class="mb-4 media-preview"
						/>
					</template>

					<!-- GIF (Giphy ID) -->
					<template v-if="mediaType === 'gif'">
						<v-text-field
							v-model="form.giphy_id"
							label="Giphy ID"
							placeholder="Ex: 3o7TKSjRrfIPjeiVyM"
							outlined
							dense
							:error-messages="errors.giphy_id"
							hide-details="auto"
							class="mb-4"
						/>
						<v-img
							v-if="form.giphy_id"
							:src="`https://media.giphy.com/media/${form.giphy_id}/giphy.gif`"
							max-height="200"
							contain
							class="mb-4 media-preview"
						/>
					</template>

					<!-- YouTube / Story video URL -->
					<template v-if="mediaType === 'youtube' || mediaType === 'video_url'">
						<v-text-field
							v-model="form.video_url"
							:label="contentType === 'story' ? 'Video URL' : 'YouTube URL'"
							:placeholder="contentType === 'story' ? 'https://...' : 'https://youtube.com/watch?v=...'"
							outlined
							dense
							:error-messages="errors.video_url"
							hide-details="auto"
							class="mb-4"
						/>
						<v-img
							v-if="contentType === 'feed' && youtubeThumbnail"
							:src="youtubeThumbnail"
							max-height="200"
							contain
							class="mb-4 media-preview"
						/>
					</template>

					<!-- Video Upload (feed only) -->
					<template v-if="mediaType === 'video'">
						<v-file-input
							v-model="form.video_file"
							label="Video file"
							accept="video/mp4,video/webm,video/quicktime"
							outlined
							dense
							prepend-icon=""
							:prepend-inner-icon="icons.mdiVideo"
							:error-messages="errors.video_file"
							hide-details="auto"
							class="mb-4"
							@change="onVideoChange"
						/>
						<video
							v-if="videoPreview"
							:src="videoPreview"
							controls
							class="mb-4 media-preview"
						/>
						<v-select
							v-model="form.video_orientation"
							:items="orientationOptions"
							label="Orientation"
							outlined
							dense
							hide-details="auto"
							class="mb-4"
						/>
					</template>

					<!-- Story expiry -->
					<v-text-field
						v-if="contentType === 'story'"
						v-model="form.expires_at"
						label="Expires at (optional, defaults to 24h)"
						type="datetime-local"
						outlined
						dense
						:error-messages="errors.expires_at"
						hide-details="auto"
						class="mb-4"
					/>

					<!-- Allow Comments (feed only) -->
					<v-switch
						v-if="contentType === 'feed'"
						v-model="form.allow_comments"
						label="Allow comments"
						dense
						hide-details
						class="mt-0"
					/>
				</v-card-text>

				<!-- Loading / Success -->
				<v-card-text
					v-if="loading"
					class="d-flex align-center pt-0 pb-3"
				>
					<v-progress-circular
						indeterminate
						size="16"
						width="2"
						color="primary"
						class="me-2"
					/>
					<span class="text-caption">Publishing...</span>
				</v-card-text>

				<v-card-text
					v-else-if="successMsg"
					class="d-flex align-center pt-0 pb-3"
				>
					<v-icon
						small
						color="success"
						class="me-1"
					>
						{{ icons.mdiCheckCircleOutline }}
					</v-icon>
					<span class="text-caption success--text">{{ successMsg }}</span>
				</v-card-text>

				<v-card-text
					v-else-if="errorMsg"
					class="d-flex align-center pt-0 pb-3"
				>
					<v-icon
						small
						color="error"
						class="me-1"
					>
						{{ icons.mdiAlertCircleOutline }}
					</v-icon>
					<span class="text-caption error--text">{{ errorMsg }}</span>
				</v-card-text>

				<v-card-actions class="pt-0">
					<v-spacer />
					<v-btn
						text
						small
						:disabled="loading"
						@click="closeDialog"
					>
						Cancel
					</v-btn>
					<v-btn
						color="primary"
						small
						:disabled="loading || !canSubmit"
						@click="publish"
					>
						<v-icon
							small
							class="me-1"
						>
							{{ icons.mdiSend }}
						</v-icon>
						Publish
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import {
	mdiPlusCircleOutline,
	mdiViewDashboardOutline,
	mdiBookOpenPageVariantOutline,
	mdiText,
	mdiImage,
	mdiGif,
	mdiYoutube,
	mdiVideo,
	mdiSend,
	mdiCheckCircleOutline,
	mdiAlertCircleOutline,
} from '@mdi/js'
import NetworkAccountSelect from '@/@core/components/NetworkAccountSelect.vue'
import { createFeedPost } from '@/services/community/feedPublisher'
import { createStory } from '@/services/community/storyPublisher'

const defaultForm = () => ({
	userId: null,
	content: '',
	image: null,
	giphy_id: '',
	video_url: '',
	video_file: null,
	video_orientation: 'horizontal',
	allow_comments: true,
	expires_at: '',
})

export default {
	components: {
		NetworkAccountSelect,
	},
	data() {
		return {
			dialog: false,
			dialogKey: 0,
			loading: false,
			imagePreview: null,
			videoPreview: null,
			successMsg: '',
			errorMsg: '',
			errors: {},
			contentType: 'feed', // feed | story
			mediaType: 'text',
			form: defaultForm(),
			orientationOptions: [
				{ text: 'Horizontal', value: 'horizontal' },
				{ text: 'Vertical', value: 'vertical' },
				{ text: 'Square', value: 'square' },
			],
			icons: {
				mdiPlusCircleOutline,
				mdiViewDashboardOutline,
				mdiBookOpenPageVariantOutline,
				mdiText,
				mdiImage,
				mdiGif,
				mdiYoutube,
				mdiVideo,
				mdiSend,
				mdiCheckCircleOutline,
				mdiAlertCircleOutline,
			},
		}
	},
	computed: {
		canSubmit() {
			if (!this.form.userId) return false
			if (this.contentType === 'story') {
				return (this.mediaType === 'image' && this.form.image) ||
					(this.mediaType === 'video_url' && this.form.video_url)
			}
			return !!this.form.content
		},
		youtubeThumbnail() {
			if (!this.form.video_url) return null
			const patterns = [
				/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
				/(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
				/(?:youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
				/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
				/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
			]
			for (const pattern of patterns) {
				const match = this.form.video_url.match(pattern)
				if (match) return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`
			}
			return null
		},
	},
	watch: {
		contentType(next) {
			// Reset to a media type that's valid for the newly selected content type.
			this.mediaType = next === 'story' ? 'image' : 'text'
		},
	},
	methods: {
		openDialog() {
			this.resetForm()
			this.dialogKey++
			this.dialog = true
		},
		closeDialog() {
			if (this.loading) return
			this.dialog = false
		},
		resetForm() {
			this.revokePreviewUrls()
			this.form = defaultForm()
			this.contentType = 'feed'
			this.mediaType = 'text'
			this.imagePreview = null
			this.videoPreview = null
			this.errors = {}
			this.successMsg = ''
			this.errorMsg = ''
			this.loading = false
		},
		onImageChange(file) {
			this.revokePreviewUrls()
			this.imagePreview = file ? URL.createObjectURL(file) : null
		},
		onVideoChange(file) {
			this.revokePreviewUrls()
			this.videoPreview = file ? URL.createObjectURL(file) : null
		},
		revokePreviewUrls() {
			if (this.imagePreview) URL.revokeObjectURL(this.imagePreview)
			if (this.videoPreview) URL.revokeObjectURL(this.videoPreview)
		},
		async publish() {
			if (!this.canSubmit || this.loading) return

			this.errors = {}
			this.successMsg = ''
			this.errorMsg = ''
			this.loading = true

			try {
				if (this.contentType === 'story') {
					await this.publishStory()
					this.successMsg = 'Story published successfully!'
				} else {
					await this.publishFeedPost()
					this.successMsg = 'Post published successfully!'
				}

				setTimeout(() => {
					this.resetForm()
					this.dialog = false
				}, 1500)
			} catch (err) {
				if (err?.response?.status === 422 && err?.response?.data?.errors) {
					this.errors = err.response.data.errors
				}
				const action = this.contentType === 'story' ? 'publish story' : 'publish post'
				this.errorMsg = err?.response?.data?.message || err?.message || `Failed to ${action}.`
			} finally {
				this.loading = false
			}
		},
		async publishFeedPost() {
			const fd = new FormData()
			fd.append('user_id', this.form.userId)
			fd.append('content', this.form.content)
			fd.append('allow_comments', this.form.allow_comments ? '1' : '0')

			if (this.mediaType === 'image' && this.form.image) {
				fd.append('image', this.form.image)
			} else if (this.mediaType === 'gif' && this.form.giphy_id) {
				fd.append('giphy_id', this.form.giphy_id)
			} else if (this.mediaType === 'youtube' && this.form.video_url) {
				fd.append('video_type', 'youtube')
				fd.append('video_url', this.form.video_url)
			} else if (this.mediaType === 'video' && this.form.video_file) {
				fd.append('video_type', 'upload')
				fd.append('video_file', this.form.video_file)
				fd.append('video_orientation', this.form.video_orientation)
			}

			await createFeedPost(fd)
		},
		async publishStory() {
			const fd = new FormData()
			fd.append('user_id', this.form.userId)
			if (this.form.content) fd.append('caption', this.form.content)
			if (this.form.expires_at) fd.append('expires_at', this.form.expires_at)

			if (this.mediaType === 'image' && this.form.image) {
				fd.append('image', this.form.image)
			} else if (this.mediaType === 'video_url' && this.form.video_url) {
				fd.append('video_url', this.form.video_url)
			}

			await createStory(fd)
		},
	},
}
</script>

<style scoped>
.content-publisher-btn {
	cursor: pointer;
	opacity: 0.7;
	transition: opacity 0.2s;
}
.content-publisher-btn:hover {
	opacity: 1;
}
.media-preview {
	max-width: 100%;
	max-height: 200px;
	background: rgba(0, 0, 0, 0.05);
	border: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
